import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Ban,
  XCircle,
  Clock,
  ShieldQuestion,
  IndianRupee,
  Search,
  ArrowUpRight,
} from 'lucide-react';

import { DataTable, type Column } from '@/components/common/DataTable';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { KpiCard } from '@/components/common/KpiCard';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cancelsService } from '@/services/cancels';
import type { CancelRequest, CancelRequestStatus } from '@/types/cancels';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { CancelRequestDetailSheet } from './CancelRequestDetailSheet';

type StatusFilter = 'all' | CancelRequestStatus;

const STATUS_LABEL: Record<CancelRequestStatus, string> = {
  PENDING_REVIEW: 'Pending review',
  APPROVED: 'Approved',
  REFUND_INITIATED: 'Refunding',
  REFUND_CONFIRMED: 'Refunded',
  REFUND_FAILED: 'Refund failed',
  REJECTED: 'Rejected',
  CLOSED: 'Closed',
};

const STATUS_TONE: Record<
  CancelRequestStatus,
  'muted' | 'warning' | 'success' | 'destructive' | 'info'
> = {
  PENDING_REVIEW: 'warning',
  APPROVED: 'info',
  REFUND_INITIATED: 'info',
  REFUND_CONFIRMED: 'success',
  REFUND_FAILED: 'destructive',
  REJECTED: 'destructive',
  CLOSED: 'muted',
};

export function CancelRequestsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<CancelRequest | null>(null);
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cancels', { q: debounced }],
    queryFn: () =>
      cancelsService.list({ search: debounced || undefined, pageSize: 200 }),
    refetchInterval: 30_000,
  });

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      PENDING_REVIEW: 0,
      APPROVED: 0,
      REFUND_INITIATED: 0,
      REFUND_CONFIRMED: 0,
      REFUND_FAILED: 0,
      REJECTED: 0,
      CLOSED: 0,
    };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const pending = counts.PENDING_REVIEW;
    const refunding = counts.REFUND_INITIATED;
    const failed = counts.REFUND_FAILED;
    let totalRefundedValue = 0;
    for (const r of allRows) {
      if (r.status === 'REFUND_CONFIRMED' && r.refundAmount) {
        totalRefundedValue += r.refundAmount;
      }
    }
    return { pending, refunding, failed, totalRefundedValue };
  }, [allRows, counts]);

  const tabs: StatusTabOption[] = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'PENDING_REVIEW', label: 'Pending', count: counts.PENDING_REVIEW },
    { value: 'REFUND_INITIATED', label: 'Refunding', count: counts.REFUND_INITIATED },
    { value: 'REFUND_CONFIRMED', label: 'Refunded', count: counts.REFUND_CONFIRMED },
    { value: 'REFUND_FAILED', label: 'Failed', count: counts.REFUND_FAILED },
    { value: 'REJECTED', label: 'Rejected', count: counts.REJECTED },
  ];

  const columns: Column<CancelRequest>[] = [
    {
      key: 'cancelNumber',
      header: 'Cancel #',
      width: '160px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.cancelNumber}</div>
          {r.externalRef && (
            <div className="font-mono text-[10.5px] text-muted-foreground">{r.externalRef}</div>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Requested at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] text-foreground">
            {r.salesOrder.orderNumber}
          </div>
          <div className="text-[10.5px] text-muted-foreground">{r.salesOrder.status}</div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{r.customer.name}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.customer.code}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Refund amount',
      align: 'right',
      width: '140px',
      cell: (r) =>
        r.refundAmount != null ? (
          <span className="font-mono text-[12.5px] tabular-nums">
            {fmt.money(r.refundAmount, r.refundCurrency ?? 'INR')}
          </span>
        ) : (
          <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
            {fmt.money(r.salesOrder.grandTotal ?? r.salesOrder.totalAmount, r.salesOrder.currency)}
            <span className="ml-1 text-[10px] text-muted-foreground/70">(est)</span>
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '160px',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'reviewer',
      header: 'Reviewer',
      width: '140px',
      cell: (r) =>
        r.assignedReviewerId ? (
          <span className="text-[11.5px] text-muted-foreground">
            assigned
            {r.assignedAt && (
              <span className="ml-1 text-[10.5px] text-muted-foreground/70">
                ({fmt.relative(r.assignedAt)})
              </span>
            )}
          </span>
        ) : (
          <span className="text-[11.5px] text-amber-600">Unassigned</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '60px',
      hoverReveal: true,
      cell: (r) => (
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => setSelected(r)}
          aria-label="Open detail"
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Cancel Requests"
        count={counts.all}
        description="Customer-initiated order cancels — review, approve, watch the portal drive the refund."
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Pending review"
          value={fmt.number(summary.pending)}
          tone={summary.pending ? 'warning' : 'default'}
          icon={ShieldQuestion}
        />
        <KpiCard label="Refunding" value={fmt.number(summary.refunding)} tone="info" icon={Clock} />
        <KpiCard
          label="Refund failed"
          value={fmt.number(summary.failed)}
          tone={summary.failed ? 'destructive' : 'default'}
          icon={XCircle}
        />
        <KpiCard
          label="Refunded value (window)"
          value={fmt.inr(summary.totalRefundedValue)}
          tone="success"
          icon={IndianRupee}
        />
      </div>

      <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cancel # or external ref …"
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          onRowClick={setSelected}
          density="compact"
          emptyTitle={filter === 'all' ? 'No cancel requests yet' : `No ${STATUS_LABEL[filter as CancelRequestStatus].toLowerCase()} cancels`}
          emptyDescription={
            filter === 'all'
              ? 'When a customer asks to cancel via the portal, the request lands here for review.'
              : 'Try a different status filter.'
          }
          emptyAction={
            filter === 'all' ? null : (
              <Badge variant="muted" className="gap-1 text-[11px]">
                <Ban className="h-3 w-3" />
                Filtered view
              </Badge>
            )
          }
        />
      )}

      <CancelRequestDetailSheet
        cancelRequest={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}

export { STATUS_LABEL as CANCEL_STATUS_LABEL, STATUS_TONE as CANCEL_STATUS_TONE };
